import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Pencil, Trash2, Ticket } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { AdminListPage } from '@/features/admin/components/AdminListPage'
import { useAdminEvents } from '../hooks/useAdminEvents'
import type { EventDocument } from '@/types'

export function AdminEventsPage() {
  const navigate = useNavigate()
  const { events, loading, error, deleteEvent } = useAdminEvents()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<(EventDocument & { id: string }) | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  async function handleDelete() {
    if (!deleteTarget) return
    setDeletingId(deleteTarget.id)
    try {
      await deleteEvent(deleteTarget.id)
    } finally {
      setDeletingId(null)
      setDeleteTarget(null)
      setDialogOpen(false)
    }
  }

  function openDeleteDialog(event: EventDocument & { id: string }) {
    setDeleteTarget(event)
    setDialogOpen(true)
  }

  return (
    <AdminListPage
      title="Events"
      action={
        <Button asChild>
          <Link to="/admin/events/new">Add Event</Link>
        </Button>
      }
      error={error}
      loading={loading}
      skeletonRows={5}
      dialog={
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger className="hidden" />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Event</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-foreground">{deleteTarget?.title}</span>? This
              action cannot be undone.
            </p>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDialogOpen(false)
                  setDeleteTarget(null)
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleDelete()}
                disabled={deletingId !== null}
              >
                {deletingId !== null ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Venue</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Published</TableHead>
            <TableHead>Order</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <MultiButtonGroup>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  No events yet. Add one to get started.
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="font-medium">{event.title}</TableCell>
                  <TableCell className="text-sm">
                    {event.eventDate.toDate().toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{event.venue}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        event.status === 'published'
                          ? 'default'
                          : event.status === 'cancelled'
                            ? 'destructive'
                            : 'secondary'
                      }
                    >
                      {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={event.isPublished ? 'default' : 'secondary'}>
                      {event.isPublished ? 'Published' : 'Draft'}
                    </Badge>
                  </TableCell>
                  <TableCell>{event.order}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end">
                      <MultiButton
                        variant="outline"
                        size="sm"
                        items={[
                          {
                            id: 'edit',
                            icon: Pencil,
                            label: 'Edit',
                            onClick: () => void navigate(`/admin/events/${event.id}/edit`),
                          },
                          {
                            id: 'tickets',
                            icon: Ticket,
                            label: 'Tickets',
                            onClick: () => void navigate(`/admin/events/${event.id}/tickets`),
                          },
                          {
                            id: 'delete',
                            icon: Trash2,
                            label: 'Delete',
                            onClick: () => openDeleteDialog(event),
                            disabled: deletingId === event.id,
                            className: 'text-destructive hover:text-destructive',
                          },
                        ]}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </MultiButtonGroup>
      </Table>
    </AdminListPage>
  )
}
